export type Language = "en" | "de" | "es" | "fr" | "pt";

export const languages: { code: Language; name: string; flag: string }[] = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "de", name: "German", flag: "🇩🇪" },
  { code: "es", name: "Spanish", flag: "🇪🇸" },
  { code: "fr", name: "French", flag: "🇫🇷" },
  { code: "pt", name: "Portuguese", flag: "🇵🇹" },
];

export const translations = {
  en: {
    nav: {
      features: "Features",
      pricing: "Pricing",
      testimonials: "Testimonials",
      signUp: "Sign up",
      dashboard: "Dashboard",
      startRitual: "Start Your Ritual",
    },
    hero: {
      badge: "Begin your transformation",
      title1: "Your daily ritual.",
      title2: "Your evolution.",
      subtitle: "Transform your mornings, elevate your mindset, and evolve into the best version of yourself through the power of intentional daily rituals.",
      trialText: "7-day free trial • Cancel anytime",
      stats: {
        users: "Active ritualists",
        rating: "App Store rating",
        days: "To build a habit",
      },
    },
    whatIs: {
      title: "What is Evooluir?",
      subtitle: "Evooluir is your personal guide to building meaningful daily rituals that transform your life. Based on ancient wisdom and modern science.",
      pillars: {
        mindfulness: {
          title: "Mindfulness",
          description: "Cultivate presence and awareness through guided meditation and breathing exercises.",
        },
        intention: {
          title: "Intention",
          description: "Set clear daily intentions that align with your values and long-term goals.",
        },
        reflection: {
          title: "Reflection",
          description: "End each day with gratitude and insights that accelerate your personal growth.",
        },
      },
    },
    appPreview: {
      ready: "Ready for your ritual?",
      dayStreak: "Day Streak",
      totalDays: "Total Days",
      title: "Experience the app",
      subtitle: "Beautiful, intuitive design that makes your daily ritual a joy. Track your progress, unlock insights, and watch yourself evolve.",
      features: {
        guided: "Guided morning & evening rituals",
        personalized: "Personalized recommendations",
        analytics: "Beautiful progress analytics",
        offline: "Offline access",
      },
    },
    benefits: {
      title: "The power of daily rituals",
      subtitle: "Science-backed benefits that compound over time. Start small, stay consistent, and watch your life transform.",
      items: {
        clarity: {
          title: "Mental Clarity",
          description: "Clear your mind and start each day with focus and purpose.",
        },
        stress: {
          title: "Stress Reduction",
          description: "Build resilience and find calm amidst life's chaos.",
        },
        sleep: {
          title: "Better Sleep",
          description: "Evening rituals that prepare your mind and body for rest.",
        },
        productivity: {
          title: "Increased Productivity",
          description: "Harness peak performance through intentional routines.",
        },
        relationships: {
          title: "Deeper Relationships",
          description: "Cultivate presence and connection in your interactions.",
        },
        growth: {
          title: "Personal Growth",
          description: "Continuous evolution through daily reflection and learning.",
        },
      },
    },
    unlock: {
      title: "Unlock your full potential",
      subtitle: "Premium features designed to accelerate your transformation journey.",
      premiumAccess: "Premium Access",
      premiumDesc: "Everything you need to build lasting rituals",
      features: {
        unlimited: "Unlimited guided sessions",
        analytics: "Advanced analytics & insights",
        personalized: "Personalized ritual plans",
        exclusive: "Exclusive content library",
        offline: "Offline access",
        support: "Priority support",
      },
    },
    testimonials: {
      title: "Loved by thousands",
      subtitle: "Join a community of people transforming their lives, one ritual at a time.",
      items: [
        {
          quote: "Evooluir changed my mornings completely. I used to dread waking up, now I look forward to my ritual. It's become the foundation of my day.",
          author: "Sarah M.",
          role: "Entrepreneur",
        },
        {
          quote: "The evening reflection feature is incredible. I've gained so much clarity about what matters in my life. My stress levels have dropped significantly.",
          author: "Michael R.",
          role: "Software Engineer",
        },
        {
          quote: "I've tried many wellness apps, but Evooluir is different. It's not just an app—it's a companion on my journey of self-discovery.",
          author: "Emma L.",
          role: "Teacher",
        },
      ],
    },
    pricing: {
      title: "Simple, transparent pricing",
      subtitle: "Invest in your wellbeing. Cancel anytime. Start your 7-day free trial today.",
      monthly: "Monthly",
      yearly: "Yearly",
      save: "Save 33%",
      mostPopular: "Most Popular",
      perMonth: "/month",
      billedAnnually: "Billed annually at $119.88",
      startRitual: "Start Your Ritual",
      trialText: "7-day free trial • Cancel anytime",
      guarantee: "30-Day Money Back Guarantee",
      guaranteeDesc: "Not satisfied? We'll refund you, no questions asked.",
      features: [
        "Unlimited guided ritual sessions",
        "Morning & evening guided practices",
        "Personalized ritual recommendations",
        "Advanced progress analytics",
        "Exclusive journaling prompts",
        "Offline access to content",
        "Priority customer support",
        "New content added weekly",
      ],
    },
    finalCta: {
      badge: "Your transformation awaits",
      title: "Ready to",
      titleHighlight: "evolve",
      subtitle: "Every great transformation begins with a single step. Your ritual awaits. Start your 7-day free trial and discover what's possible.",
      joinCount: "Join 50,000+ people transforming their lives",
      featuredIn: "Featured in",
    },
    footer: {
      tagline: "Transform your daily rituals",
      product: "Product",
      features: "Features",
      pricing: "Pricing",
      download: "Download",
      company: "Company",
      about: "About",
      careers: "Careers",
      press: "Press",
      support: "Support",
      helpCenter: "Help Center",
      contact: "Contact",
      privacy: "Privacy",
      terms: "Terms",
      copyright: "All rights reserved.",
      newsletter: {
        title: "Stay in the loop",
        subtitle: "Be the first to get updates on our latest content, special offers, and new features.",
        placeholder: "Email address",
        button: "Subscribe",
        disclaimer: "By signing up, you're agreeing to receive marketing emails. You can unsubscribe at any time.",
      },
      getStarted: "Get Started",
      tryFree: "Try 7-days free",
      ourPlans: "Our plans",
      sendGift: "Send a gift",
      redeemCode: "Redeem a code",
      ourContent: "Our Content",
      rituals: "Daily rituals",
      meditation: "Meditation",
      mindfulness: "Mindfulness",
      sleepContent: "Sleep content",
      articles: "Articles",
      blog: "Blog",
      faq: "FAQ",
      accessibility: "Accessibility",
      myAccount: "My Account",
      login: "Login",
      getApp: "Get the app",
      consumerHealth: "Consumer Health Data",
      privacyChoices: "Your privacy choices",
    },
    signup: {
      title: "Sign up",
      hasAccount: "Already have an account?",
      logIn: "Log in",
      getExperience: "Get the full Evooluir experience for",
      today: "today",
      perMonth: "per month",
      billedAnnually: "billed annually at",
      afterTrial: "after your 7-day trial",
      cancelAnytime: "Cancel anytime",
      benefit1: "Access our entire library of rituals, meditations, and mindfulness exercises",
      benefit2: "Get personalized wellness content delivered daily",
      benefit3: "Daily inspirational content to start your day right",
      name: "Name",
      emailAddress: "Email address",
      password: "Password",
      characters: "characters",
      newsletterQuestion: "Want to stay up to date on the latest?",
      newsletterSubtext: "Join our email list and be the first to know about special offers.",
      yes: "Yes",
      no: "No",
      areYouSure: "Are you sure?",
      missOut: "You'll miss out on exclusive discounts and the latest news from Evooluir.",
      agreeToTerms: "I agree to Evooluir's",
      termsLink: "Terms & Conditions",
      andAcknowledge: "and acknowledge the",
      privacyLink: "Privacy Policy",
      createAccount: "Create account",
      processing: "Processing...",
    },
    checkout: {
      signUpTitle: "Create your account",
      signUpSubtitle: "Sign up to start your wellness journey and unlock your subscription.",
      emailLabel: "Email",
      emailPlaceholder: "Enter your email",
      passwordLabel: "Password",
      passwordPlaceholder: "Create a password",
      signUpButton: "Sign up",
      orContinueWith: "Or continue with",
      continueSignUp: "Continue to Sign Up",
      continueEmail: "Continue with Email",
      continueGoogle: "Continue with Google",
      continueApple: "Continue with Apple",
      thenSelectPlan: "Then select your plan",
      afterSignupText: "After signing up, you'll select your plan and complete payment to start your wellness journey.",
      termsText: "By signing up, you agree to our Terms of Service and Privacy Policy.",
      planTitle: "Your selected plan",
      planMonthly: "Monthly Plan",
      planYearly: "Yearly Plan",
      perMonth: "/month",
      billedAnnually: "Billed annually",
      startTrial: "Start 7-day free trial",
      processing: "Processing...",
      included: "What's included:",
      features: [
        "Unlimited guided sessions",
        "Morning & evening rituals",
        "Personalized recommendations",
        "Progress analytics",
      ],
    },
    success: {
      welcomeBadge: "Welcome to Evooluir",
      title: "Your ritual begins now",
      subtitle: "Thank you for joining our community of conscious individuals. Your transformation journey starts today.",
      premiumTitle: "Evooluir Premium",
      subscriptionActive: "Your subscription is now active",
      features: [
        "Unlimited access to all ritual sessions",
        "Premium audio-guided meditations",
        "Advanced progress analytics",
        "Exclusive content updated weekly",
      ],
      downloadTitle: "Download the app",
      downloadSubtitle: "Continue your ritual on mobile",
      downloadOn: "Download on the",
      appStore: "App Store",
      getItOn: "Get it on",
      googlePlay: "Google Play",
      goToDashboard: "Go to Dashboard",
    },
    notFound: {
      title: "Page not found",
      subtitle: "The page you're looking for doesn't exist or has moved.",
      backHome: "Back to home",
    },
    cancel: {
      title: "Checkout canceled",
      subtitle: "No worries, you haven't been charged. Pick a plan whenever you're ready.",
      backToPlans: "Back to plans",
    },
    dashboard: {
      welcome: "Welcome back",
      subtitle: "Continue your journey of transformation",
      subscription: "Subscription",
      status: "Status",
      active: "Active",
      inactive: "Inactive",
      plan: "Plan",
      monthly: "Monthly",
      yearly: "Yearly",
      nextBilling: "Next billing",
      manage: "Manage",
      noSubscription: "You don't have an active subscription yet.",
      quickStats: "Quick Stats",
      currentStreak: "Current Streak",
      days: "days",
      totalRituals: "Total Rituals",
      rituals: "rituals",
      memberSince: "Member Since",
      getApp: "Get the App",
      getAppDesc: "Continue your ritual journey on mobile",
      account: "Account",
      settings: "Settings",
      billingHistory: "Billing History",
      logout: "Log out",
    },
  },
  de: {
    nav: {
      features: "Funktionen",
      pricing: "Preise",
      testimonials: "Bewertungen",
      signUp: "Registrieren",
      dashboard: "Dashboard",
      startRitual: "Starte dein Ritual",
    },
    hero: {
      badge: "Beginne deine Transformation",
      title1: "Dein tägliches Ritual.",
      title2: "Deine Evolution.",
      subtitle: "Transformiere deine Morgen, erhebe deine Denkweise und entwickle dich zur besten Version deiner selbst durch die Kraft bewusster täglicher Rituale.",
      trialText: "7 Tage kostenlos testen • Jederzeit kündbar",
      stats: {
        users: "Aktive Ritualisten",
        rating: "App Store Bewertung",
        days: "Um eine Gewohnheit aufzubauen",
      },
    },
    whatIs: {
      title: "Was ist Evooluir?",
      subtitle: "Evooluir ist dein persönlicher Guide zum Aufbau bedeutungsvoller täglicher Rituale, die dein Leben transformieren. Basierend auf alter Weisheit und moderner Wissenschaft.",
      pillars: {
        mindfulness: {
          title: "Achtsamkeit",
          description: "Kultiviere Präsenz und Bewusstsein durch geführte Meditation und Atemübungen.",
        },
        intention: {
          title: "Intention",
          description: "Setze klare tägliche Absichten, die mit deinen Werten und langfristigen Zielen übereinstimmen.",
        },
        reflection: {
          title: "Reflexion",
          description: "Beende jeden Tag mit Dankbarkeit und Erkenntnissen, die dein persönliches Wachstum beschleunigen.",
        },
      },
    },
    appPreview: {
      ready: "Bereit für dein Ritual?",
      dayStreak: "Tagessträhne",
      totalDays: "Gesamttage",
      title: "Erlebe die App",
      subtitle: "Schönes, intuitives Design, das dein tägliches Ritual zur Freude macht. Verfolge deinen Fortschritt, schalte Erkenntnisse frei und beobachte deine Entwicklung.",
      features: {
        guided: "Geführte Morgen- & Abendrituale",
        personalized: "Personalisierte Empfehlungen",
        analytics: "Schöne Fortschrittsanalysen",
        offline: "Offline-Zugang",
      },
    },
    benefits: {
      title: "Die Kraft täglicher Rituale",
      subtitle: "Wissenschaftlich fundierte Vorteile, die sich über die Zeit summieren. Fang klein an, bleib konsequent und beobachte, wie sich dein Leben transformiert.",
      items: {
        clarity: {
          title: "Mentale Klarheit",
          description: "Kläre deinen Geist und starte jeden Tag mit Fokus und Ziel.",
        },
        stress: {
          title: "Stressabbau",
          description: "Baue Resilienz auf und finde Ruhe inmitten des Chaos des Lebens.",
        },
        sleep: {
          title: "Besserer Schlaf",
          description: "Abendrituale, die Geist und Körper auf die Ruhe vorbereiten.",
        },
        productivity: {
          title: "Gesteigerte Produktivität",
          description: "Nutze Spitzenleistung durch bewusste Routinen.",
        },
        relationships: {
          title: "Tiefere Beziehungen",
          description: "Kultiviere Präsenz und Verbindung in deinen Interaktionen.",
        },
        growth: {
          title: "Persönliches Wachstum",
          description: "Kontinuierliche Entwicklung durch tägliche Reflexion und Lernen.",
        },
      },
    },
    unlock: {
      title: "Entfalte dein volles Potenzial",
      subtitle: "Premium-Funktionen, die deine Transformationsreise beschleunigen.",
      premiumAccess: "Premium-Zugang",
      premiumDesc: "Alles, was du brauchst, um dauerhafte Rituale aufzubauen",
      features: {
        unlimited: "Unbegrenzte geführte Sitzungen",
        analytics: "Erweiterte Analysen & Erkenntnisse",
        personalized: "Personalisierte Ritualpläne",
        exclusive: "Exklusive Inhaltsbibliothek",
        offline: "Offline-Zugang",
        support: "Prioritäts-Support",
      },
    },
    testimonials: {
      title: "Von Tausenden geliebt",
      subtitle: "Schließe dich einer Gemeinschaft von Menschen an, die ihr Leben transformieren, ein Ritual nach dem anderen.",
      items: [
        {
          quote: "Evooluir hat meine Morgen komplett verändert. Früher habe ich das Aufwachen gefürchtet, jetzt freue ich mich auf mein Ritual. Es ist zum Fundament meines Tages geworden.",
          author: "Sarah M.",
          role: "Unternehmerin",
        },
        {
          quote: "Die Abendreflexionsfunktion ist unglaublich. Ich habe so viel Klarheit darüber gewonnen, was in meinem Leben wichtig ist. Mein Stresslevel ist deutlich gesunken.",
          author: "Michael R.",
          role: "Softwareentwickler",
        },
        {
          quote: "Ich habe viele Wellness-Apps ausprobiert, aber Evooluir ist anders. Es ist nicht nur eine App – es ist ein Begleiter auf meiner Reise der Selbstentdeckung.",
          author: "Emma L.",
          role: "Lehrerin",
        },
      ],
    },
    pricing: {
      title: "Einfache, transparente Preise",
      subtitle: "Investiere in dein Wohlbefinden. Jederzeit kündbar. Starte heute deine 7-tägige kostenlose Testversion.",
      monthly: "Monatlich",
      yearly: "Jährlich",
      save: "Spare 33%",
      mostPopular: "Am beliebtesten",
      perMonth: "/Monat",
      billedAnnually: "Jährlich abgerechnet mit $119,88",
      startRitual: "Starte dein Ritual",
      trialText: "7 Tage kostenlos testen • Jederzeit kündbar",
      guarantee: "30-Tage Geld-zurück-Garantie",
      guaranteeDesc: "Nicht zufrieden? Wir erstatten dir den Betrag, ohne Fragen zu stellen.",
      features: [
        "Unbegrenzte geführte Ritualsitzungen",
        "Morgen- & Abendpraktiken",
        "Personalisierte Ritualempfehlungen",
        "Erweiterte Fortschrittsanalysen",
        "Exklusive Tagebuchanregungen",
        "Offline-Zugang zu Inhalten",
        "Prioritäts-Kundensupport",
        "Wöchentlich neue Inhalte",
      ],
    },
    finalCta: {
      badge: "Deine Transformation wartet",
      title: "Bereit zu",
      titleHighlight: "evolvieren",
      subtitle: "Jede große Transformation beginnt mit einem einzigen Schritt. Dein Ritual wartet. Starte deine 7-tägige kostenlose Testversion und entdecke, was möglich ist.",
      joinCount: "Schließe dich 50.000+ Menschen an, die ihr Leben transformieren",
      featuredIn: "Bekannt aus",
    },
    footer: {
      tagline: "Transformiere deine täglichen Rituale",
      product: "Produkt",
      features: "Funktionen",
      pricing: "Preise",
      download: "Download",
      company: "Unternehmen",
      about: "Über uns",
      careers: "Karriere",
      press: "Presse",
      support: "Support",
      helpCenter: "Hilfecenter",
      contact: "Kontakt",
      privacy: "Datenschutz",
      terms: "AGB",
      copyright: "Alle Rechte vorbehalten.",
      newsletter: {
        title: "Bleib auf dem Laufenden",
        subtitle: "Erfahre als Erstes von unseren neuesten Inhalten, Sonderangeboten und neuen Funktionen.",
        placeholder: "E-Mail-Adresse",
        button: "Abonnieren",
        disclaimer: "Mit der Anmeldung erklärst du dich damit einverstanden, Marketing-E-Mails zu erhalten. Du kannst dich jederzeit abmelden.",
      },
      getStarted: "Loslegen",
      tryFree: "7 Tage kostenlos testen",
      ourPlans: "Unsere Pläne",
      sendGift: "Geschenk senden",
      redeemCode: "Code einlösen",
      ourContent: "Unsere Inhalte",
      rituals: "Tägliche Rituale",
      meditation: "Meditation",
      mindfulness: "Achtsamkeit",
      sleepContent: "Schlafinhalte",
      articles: "Artikel",
      blog: "Blog",
      faq: "FAQ",
      accessibility: "Barrierefreiheit",
      myAccount: "Mein Konto",
      login: "Anmelden",
      getApp: "Hole die App",
      consumerHealth: "Verbrauchergesundheitsdaten",
      privacyChoices: "Deine Datenschutzoptionen",
    },
    signup: {
      title: "Registrieren",
      hasAccount: "Bereits ein Konto?",
      logIn: "Anmelden",
      getExperience: "Erhalte das vollständige Evooluir-Erlebnis für",
      today: "heute",
      perMonth: "pro Monat",
      billedAnnually: "jährlich abgerechnet mit",
      afterTrial: "nach deiner 7-tägigen Testversion",
      cancelAnytime: "Jederzeit kündbar",
      benefit1: "Zugang zu unserer gesamten Bibliothek von Ritualen, Meditationen und Achtsamkeitsübungen",
      benefit2: "Erhalte täglich personalisierte Wellness-Inhalte",
      benefit3: "Tägliche inspirierende Inhalte für einen guten Start in den Tag",
      name: "name",
      emailAddress: "E-Mail-Adresse",
      password: "Passwort",
      characters: "Zeichen",
      newsletterQuestion: "Möchtest du auf dem Laufenden bleiben?",
      newsletterSubtext: "Tritt unserer E-Mail-Liste bei und erfahre als Erster von Sonderangeboten.",
      yes: "Ja",
      no: "Nein",
      areYouSure: "Bist du sicher?",
      missOut: "Du verpasst exklusive Rabatte und die neuesten Nachrichten von Evooluir.",
      agreeToTerms: "Ich stimme den",
      termsLink: "Nutzungsbedingungen",
      andAcknowledge: "von Evooluir zu und bestätige die",
      privacyLink: "Datenschutzrichtlinie",
      createAccount: "Konto erstellen",
      processing: "Wird verarbeitet...",
    },
    checkout: {
      signUpTitle: "Erstelle dein Konto",
      signUpSubtitle: "Registriere dich, um deine Wellness-Reise zu beginnen und dein Abonnement freizuschalten.",
      emailLabel: "E-Mail",
      emailPlaceholder: "E-Mail eingeben",
      passwordLabel: "Passwort",
      passwordPlaceholder: "Passwort erstellen",
      signUpButton: "Registrieren",
      orContinueWith: "Oder weiter mit",
      continueSignUp: "Weiter zur Registrierung",
      continueEmail: "Weiter mit E-Mail",
      continueGoogle: "Weiter mit Google",
      continueApple: "Weiter mit Apple",
      thenSelectPlan: "Dann wähle deinen Plan",
      afterSignupText: "Nach der Registrierung wählst du deinen Plan und schließt die Zahlung ab, um deine Wellness-Reise zu beginnen.",
      termsText: "Mit der Registrierung stimmst du unseren Nutzungsbedingungen und Datenschutzrichtlinien zu.",
      planTitle: "Dein ausgewählter Plan",
      planMonthly: "Monatsplan",
      planYearly: "Jahresplan",
      perMonth: "/Monat",
      billedAnnually: "Jährlich abgerechnet",
      startTrial: "7-tägige Testversion starten",
      processing: "Wird verarbeitet...",
      included: "Was enthalten ist:",
      features: [
        "Unbegrenzte geführte Sitzungen",
        "Morgen- & Abendrituale",
        "Personalisierte Empfehlungen",
        "Fortschrittsanalysen",
      ],
    },
    success: {
      welcomeBadge: "Willkommen bei Evooluir",
      title: "Dein Ritual beginnt jetzt",
      subtitle: "Danke, dass du unserer Gemeinschaft bewusster Menschen beigetreten bist. Deine Transformationsreise beginnt heute.",
      premiumTitle: "Evooluir Premium",
      subscriptionActive: "Dein Abonnement ist jetzt aktiv",
      features: [
        "Unbegrenzter Zugang zu allen Ritualsitzungen",
        "Premium-Audio-geführte Meditationen",
        "Erweiterte Fortschrittsanalysen",
        "Exklusive Inhalte wöchentlich aktualisiert",
      ],
      downloadTitle: "Lade die App herunter",
      downloadSubtitle: "Setze dein Ritual auf dem Handy fort",
      downloadOn: "Herunterladen im",
      appStore: "App Store",
      getItOn: "Jetzt bei",
      googlePlay: "Google Play",
      goToDashboard: "Zum Dashboard",
    },
    notFound: {
      title: "Seite nicht gefunden",
      subtitle: "Die Seite, die du suchst, existiert nicht oder wurde verschoben.",
      backHome: "Zur Startseite",
    },
    cancel: {
      title: "Bezahlvorgang abgebrochen",
      subtitle: "Keine Sorge, dir wurde nichts berechnet. Wähle einen Plan, wann immer du bereit bist.",
      backToPlans: "Zurück zu den Plänen",
    },
    dashboard: {
      welcome: "Willkommen zurück",
      subtitle: "Setze deine Transformationsreise fort",
      subscription: "Abonnement",
      status: "Status",
      active: "Aktiv",
      inactive: "Inaktiv",
      plan: "Plan",
      monthly: "Monatlich",
      yearly: "Jährlich",
      nextBilling: "Nächste Abrechnung",
      manage: "Verwalten",
      noSubscription: "Du hast noch kein aktives Abonnement.",
      quickStats: "Schnellstatistiken",
      currentStreak: "Aktuelle Strähne",
      days: "Tage",
      totalRituals: "Gesamtrituale",
      rituals: "Rituale",
      memberSince: "Mitglied seit",
      getApp: "Hole die App",
      getAppDesc: "Setze deine Ritualreise auf dem Handy fort",
      account: "Konto",
      settings: "Einstellungen",
      billingHistory: "Abrechnungsverlauf",
      logout: "Abmelden",
    },
  },
  es: {
    nav: {
      features: "Características",
      pricing: "Precios",
      testimonials: "Testimonios",
      signUp: "Registrarse",
      dashboard: "Panel",
      startRitual: "Comienza tu ritual",
    },
    hero: {
      badge: "Comienza tu transformación",
      title1: "Tu ritual diario.",
      title2: "Tu evolución.",
      subtitle: "Transforma tus mañanas, eleva tu mentalidad y evoluciona hacia la mejor versión de ti mismo a través del poder de los rituales diarios intencionales.",
      trialText: "7 días de prueba gratis • Cancela cuando quieras",
      stats: {
        users: "Ritualistas activos",
        rating: "Calificación App Store",
        days: "Para crear un hábito",
      },
    },
    whatIs: {
      title: "¿Qué es Evooluir?",
      subtitle: "Evooluir es tu guía personal para construir rituales diarios significativos que transforman tu vida. Basado en sabiduría ancestral y ciencia moderna.",
      pillars: {
        mindfulness: {
          title: "Atención plena",
          description: "Cultiva la presencia y la consciencia a través de meditación guiada y ejercicios de respiración.",
        },
        intention: {
          title: "Intención",
          description: "Establece intenciones diarias claras que se alineen con tus valores y metas a largo plazo.",
        },
        reflection: {
          title: "Reflexión",
          description: "Termina cada día con gratitud y perspectivas que aceleren tu crecimiento personal.",
        },
      },
    },
    appPreview: {
      ready: "¿Listo para tu ritual?",
      dayStreak: "Racha de días",
      totalDays: "Días totales",
      title: "Experimenta la app",
      subtitle: "Diseño hermoso e intuitivo que hace de tu ritual diario un placer. Sigue tu progreso, desbloquea perspectivas y observa tu evolución.",
      features: {
        guided: "Rituales guiados de mañana y noche",
        personalized: "Recomendaciones personalizadas",
        analytics: "Hermosas analíticas de progreso",
        offline: "Acceso sin conexión",
      },
    },
    benefits: {
      title: "El poder de los rituales diarios",
      subtitle: "Beneficios respaldados por la ciencia que se acumulan con el tiempo. Empieza pequeño, sé constante y observa cómo se transforma tu vida.",
      items: {
        clarity: {
          title: "Claridad mental",
          description: "Despeja tu mente y comienza cada día con enfoque y propósito.",
        },
        stress: {
          title: "Reducción del estrés",
          description: "Construye resiliencia y encuentra calma en medio del caos de la vida.",
        },
        sleep: {
          title: "Mejor sueño",
          description: "Rituales nocturnos que preparan tu mente y cuerpo para descansar.",
        },
        productivity: {
          title: "Mayor productividad",
          description: "Aprovecha el máximo rendimiento a través de rutinas intencionales.",
        },
        relationships: {
          title: "Relaciones más profundas",
          description: "Cultiva la presencia y conexión en tus interacciones.",
        },
        growth: {
          title: "Crecimiento personal",
          description: "Evolución continua a través de la reflexión diaria y el aprendizaje.",
        },
      },
    },
    unlock: {
      title: "Desbloquea tu máximo potencial",
      subtitle: "Funciones premium diseñadas para acelerar tu viaje de transformación.",
      premiumAccess: "Acceso Premium",
      premiumDesc: "Todo lo que necesitas para construir rituales duraderos",
      features: {
        unlimited: "Sesiones guiadas ilimitadas",
        analytics: "Analíticas e insights avanzados",
        personalized: "Planes de rituales personalizados",
        exclusive: "Biblioteca de contenido exclusivo",
        offline: "Acceso sin conexión",
        support: "Soporte prioritario",
      },
    },
    testimonials: {
      title: "Amado por miles",
      subtitle: "Únete a una comunidad de personas que transforman sus vidas, un ritual a la vez.",
      items: [
        {
          quote: "Evooluir cambió mis mañanas por completo. Antes temía despertar, ahora espero con ansias mi ritual. Se ha convertido en la base de mi día.",
          author: "Sarah M.",
          role: "Emprendedora",
        },
        {
          quote: "La función de reflexión nocturna es increíble. He ganado tanta claridad sobre lo que importa en mi vida. Mis niveles de estrés han bajado significativamente.",
          author: "Michael R.",
          role: "Ingeniero de software",
        },
        {
          quote: "He probado muchas apps de bienestar, pero Evooluir es diferente. No es solo una app, es un compañero en mi viaje de autodescubrimiento.",
          author: "Emma L.",
          role: "Profesora",
        },
      ],
    },
    pricing: {
      title: "Precios simples y transparentes",
      subtitle: "Invierte en tu bienestar. Cancela cuando quieras. Comienza tu prueba gratuita de 7 días hoy.",
      monthly: "Mensual",
      yearly: "Anual",
      save: "Ahorra 33%",
      mostPopular: "Más popular",
      perMonth: "/mes",
      billedAnnually: "Facturado anualmente a $119.88",
      startRitual: "Comienza tu ritual",
      trialText: "7 días de prueba gratis • Cancela cuando quieras",
      guarantee: "Garantía de devolución de 30 días",
      guaranteeDesc: "¿No satisfecho? Te reembolsamos sin hacer preguntas.",
      features: [
        "Sesiones de rituales guiados ilimitados",
        "Prácticas guiadas de mañana y noche",
        "Recomendaciones de rituales personalizadas",
        "Analíticas de progreso avanzadas",
        "Sugerencias exclusivas de journaling",
        "Acceso sin conexión al contenido",
        "Soporte al cliente prioritario",
        "Nuevo contenido añadido semanalmente",
      ],
    },
    finalCta: {
      badge: "Tu transformación espera",
      title: "¿Listo para",
      titleHighlight: "evolucionar",
      subtitle: "Toda gran transformación comienza con un solo paso. Tu ritual espera. Comienza tu prueba gratuita de 7 días y descubre lo que es posible.",
      joinCount: "Únete a más de 50,000 personas transformando sus vidas",
      featuredIn: "Destacado en",
    },
    footer: {
      tagline: "Transforma tus rituales diarios",
      product: "Producto",
      features: "Características",
      pricing: "Precios",
      download: "Descargar",
      company: "Empresa",
      about: "Nosotros",
      careers: "Carreras",
      press: "Prensa",
      support: "Soporte",
      helpCenter: "Centro de ayuda",
      contact: "Contacto",
      privacy: "Privacidad",
      terms: "Términos",
      copyright: "Todos los derechos reservados.",
      newsletter: {
        title: "Mantente informado",
        subtitle: "Sé el primero en recibir actualizaciones sobre nuestro último contenido, ofertas especiales y nuevas funciones.",
        placeholder: "Correo electrónico",
        button: "Suscribirse",
        disclaimer: "Al registrarte, aceptas recibir correos de marketing. Puedes darte de baja en cualquier momento.",
      },
      getStarted: "Comenzar",
      tryFree: "Prueba 7 días gratis",
      ourPlans: "Nuestros planes",
      sendGift: "Enviar un regalo",
      redeemCode: "Canjear código",
      ourContent: "Nuestro contenido",
      rituals: "Rituales diarios",
      meditation: "Meditación",
      mindfulness: "Mindfulness",
      sleepContent: "Contenido de sueño",
      articles: "Artículos",
      blog: "Blog",
      faq: "FAQ",
      accessibility: "Accesibilidad",
      myAccount: "Mi cuenta",
      login: "Iniciar sesión",
      getApp: "Obtén la app",
      consumerHealth: "Datos de salud",
      privacyChoices: "Opciones de privacidad",
    },
    signup: {
      title: "Regístrate",
      hasAccount: "¿Ya tienes una cuenta?",
      logIn: "Inicia sesión",
      getExperience: "Obtén la experiencia completa de Evooluir por",
      today: "hoy",
      perMonth: "por mes",
      billedAnnually: "facturado anualmente a",
      afterTrial: "después de tu prueba de 7 días",
      cancelAnytime: "Cancela en cualquier momento",
      benefit1: "Accede a toda nuestra biblioteca de rituales, meditaciones y ejercicios de mindfulness",
      benefit2: "Recibe contenido de bienestar personalizado a diario",
      benefit3: "Contenido inspirador diario para empezar bien el día",
      name: "Nombre y Apellido",
      emailAddress: "Correo electrónico",
      password: "Contraseña",
      characters: "caracteres",
      newsletterQuestion: "¿Quieres estar al día con las novedades?",
      newsletterSubtext: "Únete a nuestra lista y sé el primero en conocer ofertas especiales.",
      yes: "Sí",
      no: "No",
      areYouSure: "¿Estás seguro?",
      missOut: "Te perderás descuentos exclusivos y las últimas noticias de Evooluir.",
      agreeToTerms: "Acepto los",
      termsLink: "Términos y condiciones",
      andAcknowledge: "de Evooluir y reconozco la",
      privacyLink: "Política de privacidad",
      createAccount: "Crear cuenta",
      processing: "Procesando...",
    },
    checkout: {
      signUpTitle: "Crea tu cuenta",
      signUpSubtitle: "Regístrate para comenzar tu viaje de bienestar y desbloquear tu suscripción.",
      emailLabel: "Correo electrónico",
      emailPlaceholder: "Ingresa tu correo",
      passwordLabel: "Contraseña",
      passwordPlaceholder: "Crea una contraseña",
      signUpButton: "Registrarse",
      orContinueWith: "O continuar con",
      continueSignUp: "Continuar al registro",
      continueEmail: "Continuar con correo",
      continueGoogle: "Continuar con Google",
      continueApple: "Continuar con Apple",
      thenSelectPlan: "Luego selecciona tu plan",
      afterSignupText: "Después de registrarte, seleccionarás tu plan y completarás el pago para comenzar tu viaje de bienestar.",
      termsText: "Al registrarte, aceptas nuestros Términos de servicio y Política de privacidad.",
      planTitle: "Tu plan seleccionado",
      planMonthly: "Plan mensual",
      planYearly: "Plan anual",
      perMonth: "/mes",
      billedAnnually: "Facturado anualmente",
      startTrial: "Comenzar prueba de 7 días",
      processing: "Procesando...",
      included: "Qué incluye:",
      features: [
        "Sesiones guiadas ilimitadas",
        "Rituales de mañana y noche",
        "Recomendaciones personalizadas",
        "Analíticas de progreso",
      ],
    },
    success: {
      welcomeBadge: "Bienvenido a Evooluir",
      title: "Tu ritual comienza ahora",
      subtitle: "Gracias por unirte a nuestra comunidad de individuos conscientes. Tu viaje de transformación comienza hoy.",
      premiumTitle: "Evooluir Premium",
      subscriptionActive: "Tu suscripción está activa",
      features: [
        "Acceso ilimitado a todas las sesiones de rituales",
        "Meditaciones premium guiadas por audio",
        "Analíticas de progreso avanzadas",
        "Contenido exclusivo actualizado semanalmente",
      ],
      downloadTitle: "Descarga la app",
      downloadSubtitle: "Continúa tu ritual en el móvil",
      downloadOn: "Descargar en",
      appStore: "App Store",
      getItOn: "Disponible en",
      googlePlay: "Google Play",
      goToDashboard: "Ir al panel",
    },
    notFound: {
      title: "Página no encontrada",
      subtitle: "La página que buscas no existe o se ha movido.",
      backHome: "Volver al inicio",
    },
    cancel: {
      title: "Pago cancelado",
      subtitle: "No te preocupes, no se te ha cobrado nada. Elige un plan cuando quieras.",
      backToPlans: "Volver a los planes",
    },
    dashboard: {
      welcome: "Bienvenido de vuelta",
      subtitle: "Continúa tu viaje de transformación",
      subscription: "Suscripción",
      status: "Estado",
      active: "Activo",
      inactive: "Inactivo",
      plan: "Plan",
      monthly: "Mensual",
      yearly: "Anual",
      nextBilling: "Próxima facturación",
      manage: "Gestionar",
      noSubscription: "Aún no tienes una suscripción activa.",
      quickStats: "Estadísticas rápidas",
      currentStreak: "Racha actual",
      days: "días",
      totalRituals: "Total de rituales",
      rituals: "rituales",
      memberSince: "Miembro desde",
      getApp: "Obtén la app",
      getAppDesc: "Continúa tu viaje ritual en el móvil",
      account: "Cuenta",
      settings: "Configuración",
      billingHistory: "Historial de facturación",
      logout: "Cerrar sesión",
    },
  },
  fr: {
    nav: {
      features: "Fonctionnalités",
      pricing: "Tarifs",
      testimonials: "Témoignages",
      signUp: "S'inscrire",
      dashboard: "Tableau de bord",
      startRitual: "Commence ton rituel",
    },
    hero: {
      badge: "Commence ta transformation",
      title1: "Ton rituel quotidien.",
      title2: "Ton évolution.",
      subtitle: "Transforme tes matins, élève ton état d'esprit et évolue vers la meilleure version de toi-même grâce au pouvoir des rituels quotidiens intentionnels.",
      trialText: "7 jours d'essai gratuit • Annule à tout moment",
      stats: {
        users: "Ritualistes actifs",
        rating: "Note App Store",
        days: "Pour créer une habitude",
      },
    },
    whatIs: {
      title: "Qu'est-ce qu'Evooluir?",
      subtitle: "Evooluir est ton guide personnel pour construire des rituels quotidiens significatifs qui transforment ta vie. Basé sur la sagesse ancienne et la science moderne.",
      pillars: {
        mindfulness: {
          title: "Pleine conscience",
          description: "Cultive la présence et la conscience à travers la méditation guidée et les exercices de respiration.",
        },
        intention: {
          title: "Intention",
          description: "Fixe des intentions quotidiennes claires qui s'alignent avec tes valeurs et objectifs à long terme.",
        },
        reflection: {
          title: "Réflexion",
          description: "Termine chaque jour avec gratitude et insights qui accélèrent ta croissance personnelle.",
        },
      },
    },
    appPreview: {
      ready: "Prêt pour ton rituel?",
      dayStreak: "Série de jours",
      totalDays: "Jours totaux",
      title: "Découvre l'application",
      subtitle: "Design beau et intuitif qui fait de ton rituel quotidien un plaisir. Suis ta progression, débloque des insights et observe ton évolution.",
      features: {
        guided: "Rituels guidés matin et soir",
        personalized: "Recommandations personnalisées",
        analytics: "Belles analyses de progression",
        offline: "Accès hors ligne",
      },
    },
    benefits: {
      title: "Le pouvoir des rituels quotidiens",
      subtitle: "Des avantages prouvés par la science qui s'accumulent avec le temps. Commence petit, reste constant et observe ta vie se transformer.",
      items: {
        clarity: {
          title: "Clarté mentale",
          description: "Clarifie ton esprit et commence chaque jour avec focus et objectif.",
        },
        stress: {
          title: "Réduction du stress",
          description: "Construis la résilience et trouve le calme au milieu du chaos de la vie.",
        },
        sleep: {
          title: "Meilleur sommeil",
          description: "Rituels du soir qui préparent ton esprit et ton corps au repos.",
        },
        productivity: {
          title: "Productivité accrue",
          description: "Exploite la performance maximale grâce à des routines intentionnelles.",
        },
        relationships: {
          title: "Relations plus profondes",
          description: "Cultive la présence et la connexion dans tes interactions.",
        },
        growth: {
          title: "Croissance personnelle",
          description: "Évolution continue grâce à la réflexion quotidienne et l'apprentissage.",
        },
      },
    },
    unlock: {
      title: "Libère ton plein potentiel",
      subtitle: "Fonctionnalités premium conçues pour accélérer ton parcours de transformation.",
      premiumAccess: "Accès Premium",
      premiumDesc: "Tout ce dont tu as besoin pour construire des rituels durables",
      features: {
        unlimited: "Sessions guidées illimitées",
        analytics: "Analyses et insights avancés",
        personalized: "Plans de rituels personnalisés",
        exclusive: "Bibliothèque de contenu exclusif",
        offline: "Accès hors ligne",
        support: "Support prioritaire",
      },
    },
    testimonials: {
      title: "Aimé par des milliers",
      subtitle: "Rejoins une communauté de personnes qui transforment leur vie, un rituel à la fois.",
      items: [
        {
          quote: "Evooluir a complètement changé mes matins. Avant je redoutais de me réveiller, maintenant j'attends mon rituel avec impatience. C'est devenu la base de ma journée.",
          author: "Sarah M.",
          role: "Entrepreneure",
        },
        {
          quote: "La fonction de réflexion du soir est incroyable. J'ai gagné tellement de clarté sur ce qui compte dans ma vie. Mon niveau de stress a considérablement baissé.",
          author: "Michael R.",
          role: "Ingénieur logiciel",
        },
        {
          quote: "J'ai essayé beaucoup d'applications de bien-être, mais Evooluir est différente. Ce n'est pas juste une app, c'est un compagnon dans mon voyage de découverte de soi.",
          author: "Emma L.",
          role: "Enseignante",
        },
      ],
    },
    pricing: {
      title: "Tarifs simples et transparents",
      subtitle: "Investis dans ton bien-être. Annule à tout moment. Commence ton essai gratuit de 7 jours aujourd'hui.",
      monthly: "Mensuel",
      yearly: "Annuel",
      save: "Économise 33%",
      mostPopular: "Le plus populaire",
      perMonth: "/mois",
      billedAnnually: "Facturé annuellement à $119.88",
      startRitual: "Commence ton rituel",
      trialText: "7 jours d'essai gratuit • Annule à tout moment",
      guarantee: "Garantie de remboursement de 30 jours",
      guaranteeDesc: "Pas satisfait? On te rembourse, sans poser de questions.",
      features: [
        "Sessions de rituels guidés illimitées",
        "Pratiques guidées matin et soir",
        "Recommandations de rituels personnalisées",
        "Analyses de progression avancées",
        "Invitations de journaling exclusives",
        "Accès hors ligne au contenu",
        "Support client prioritaire",
        "Nouveau contenu ajouté chaque semaine",
      ],
    },
    finalCta: {
      badge: "Ta transformation t'attend",
      title: "Prêt à",
      titleHighlight: "évoluer",
      subtitle: "Toute grande transformation commence par un seul pas. Ton rituel t'attend. Commence ton essai gratuit de 7 jours et découvre ce qui est possible.",
      joinCount: "Rejoins plus de 50 000 personnes qui transforment leur vie",
      featuredIn: "Vu dans",
    },
    footer: {
      tagline: "Transforme tes rituels quotidiens",
      product: "Produit",
      features: "Fonctionnalités",
      pricing: "Tarifs",
      download: "Télécharger",
      company: "Entreprise",
      about: "À propos",
      careers: "Carrières",
      press: "Presse",
      support: "Support",
      helpCenter: "Centre d'aide",
      contact: "Contact",
      privacy: "Confidentialité",
      terms: "Conditions",
      copyright: "Tous droits réservés.",
      newsletter: {
        title: "Reste informé",
        subtitle: "Sois le premier à recevoir nos dernières nouveautés, offres spéciales et nouvelles fonctionnalités.",
        placeholder: "Adresse e-mail",
        button: "S'abonner",
        disclaimer: "En t'inscrivant, tu acceptes de recevoir des e-mails marketing. Tu peux te désabonner à tout moment.",
      },
      getStarted: "Commencer",
      tryFree: "Essai gratuit de 7 jours",
      ourPlans: "Nos offres",
      sendGift: "Offrir un cadeau",
      redeemCode: "Utiliser un code",
      ourContent: "Notre contenu",
      rituals: "Rituels quotidiens",
      meditation: "Méditation",
      mindfulness: "Pleine conscience",
      sleepContent: "Contenu sommeil",
      articles: "Articles",
      blog: "Blog",
      faq: "FAQ",
      accessibility: "Accessibilité",
      myAccount: "Mon compte",
      login: "Connexion",
      getApp: "Télécharger l'app",
      consumerHealth: "Données de santé",
      privacyChoices: "Choix de confidentialité",
    },
    signup: {
      title: "S'inscrire",
      hasAccount: "Tu as déjà un compte ?",
      logIn: "Connecte-toi",
      getExperience: "Obtiens l'expérience complète Evooluir pour",
      today: "aujourd'hui",
      perMonth: "par mois",
      billedAnnually: "facturé annuellement à",
      afterTrial: "après ton essai de 7 jours",
      cancelAnytime: "Annule à tout moment",
      benefit1: "Accède à toute notre bibliothèque de rituels, méditations et exercices de pleine conscience",
      benefit2: "Reçois du contenu bien-être personnalisé chaque jour",
      benefit3: "Contenu inspirant quotidien pour bien démarrer ta journée",
      name: "Prénom et Nom",
      emailAddress: "Adresse e-mail",
      password: "Mot de passe",
      characters: "caractères",
      newsletterQuestion: "Tu veux rester au courant des nouveautés ?",
      newsletterSubtext: "Rejoins notre liste e-mail et sois le premier informé des offres spéciales.",
      yes: "Oui",
      no: "Non",
      areYouSure: "Tu es sûr(e) ?",
      missOut: "Tu vas manquer des réductions exclusives et les dernières nouvelles d'Evooluir.",
      agreeToTerms: "J'accepte les",
      termsLink: "Conditions générales",
      andAcknowledge: "d'Evooluir et reconnais la",
      privacyLink: "Politique de confidentialité",
      createAccount: "Créer un compte",
      processing: "Traitement...",
    },
    checkout: {
      signUpTitle: "Crée ton compte",
      signUpSubtitle: "Inscris-toi pour commencer ton parcours bien-être et débloquer ton abonnement.",
      emailLabel: "E-mail",
      emailPlaceholder: "Entre ton e-mail",
      passwordLabel: "Mot de passe",
      passwordPlaceholder: "Crée un mot de passe",
      signUpButton: "S'inscrire",
      orContinueWith: "Ou continuer avec",
      continueSignUp: "Continuer à l'inscription",
      continueEmail: "Continuer avec e-mail",
      continueGoogle: "Continuer avec Google",
      continueApple: "Continuer avec Apple",
      thenSelectPlan: "Puis choisis ton plan",
      afterSignupText: "Après l'inscription, tu sélectionneras ton plan et compléteras le paiement pour commencer ton parcours bien-être.",
      termsText: "En t'inscrivant, tu acceptes nos Conditions d'utilisation et Politique de confidentialité.",
      planTitle: "Ton plan sélectionné",
      planMonthly: "Plan mensuel",
      planYearly: "Plan annuel",
      perMonth: "/mois",
      billedAnnually: "Facturé annuellement",
      startTrial: "Commencer l'essai de 7 jours",
      processing: "Traitement...",
      included: "Ce qui est inclus:",
      features: [
        "Sessions guidées illimitées",
        "Rituels matin et soir",
        "Recommandations personnalisées",
        "Analyses de progression",
      ],
    },
    success: {
      welcomeBadge: "Bienvenue chez Evooluir",
      title: "Ton rituel commence maintenant",
      subtitle: "Merci d'avoir rejoint notre communauté d'individus conscients. Ton parcours de transformation commence aujourd'hui.",
      premiumTitle: "Evooluir Premium",
      subscriptionActive: "Ton abonnement est maintenant actif",
      features: [
        "Accès illimité à toutes les sessions de rituels",
        "Méditations audio-guidées premium",
        "Analyses de progression avancées",
        "Contenu exclusif mis à jour chaque semaine",
      ],
      downloadTitle: "Télécharge l'application",
      downloadSubtitle: "Continue ton rituel sur mobile",
      downloadOn: "Télécharger sur",
      appStore: "App Store",
      getItOn: "Disponible sur",
      googlePlay: "Google Play",
      goToDashboard: "Aller au tableau de bord",
    },
    notFound: {
      title: "Page introuvable",
      subtitle: "La page que tu cherches n'existe pas ou a été déplacée.",
      backHome: "Retour à l'accueil",
    },
    cancel: {
      title: "Paiement annulé",
      subtitle: "Pas d'inquiétude, aucun montant ne t'a été débité. Choisis une offre quand tu veux.",
      backToPlans: "Retour aux offres",
    },
    dashboard: {
      welcome: "Bon retour",
      subtitle: "Continue ton parcours de transformation",
      subscription: "Abonnement",
      status: "Statut",
      active: "Actif",
      inactive: "Inactif",
      plan: "Plan",
      monthly: "Mensuel",
      yearly: "Annuel",
      nextBilling: "Prochaine facturation",
      manage: "Gérer",
      noSubscription: "Tu n'as pas encore d'abonnement actif.",
      quickStats: "Statistiques rapides",
      currentStreak: "Série actuelle",
      days: "jours",
      totalRituals: "Total des rituels",
      rituals: "rituels",
      memberSince: "Membre depuis",
      getApp: "Obtiens l'application",
      getAppDesc: "Continue ton parcours rituel sur mobile",
      account: "Compte",
      settings: "Paramètres",
      billingHistory: "Historique de facturation",
      logout: "Déconnexion",
    },
  },
  pt: {
    nav: {
      features: "Recursos",
      pricing: "Preços",
      testimonials: "Depoimentos",
      signUp: "Cadastrar",
      dashboard: "Painel",
      startRitual: "Comece seu ritual",
    },
    hero: {
      badge: "Comece sua transformação",
      title1: "Seu ritual diário.",
      title2: "Sua evolução.",
      subtitle: "Transforme suas manhãs, eleve sua mentalidade e evolua para a melhor versão de si mesmo através do poder dos rituais diários intencionais.",
      trialText: "7 dias de teste grátis • Cancele quando quiser",
      stats: {
        users: "Ritualistas ativos",
        rating: "Avaliação App Store",
        days: "Para criar um hábito",
      },
    },
    whatIs: {
      title: "O que é Evooluir?",
      subtitle: "Evooluir é seu guia pessoal para construir rituais diários significativos que transformam sua vida. Baseado em sabedoria ancestral e ciência moderna.",
      pillars: {
        mindfulness: {
          title: "Atenção plena",
          description: "Cultive presença e consciência através de meditação guiada e exercícios de respiração.",
        },
        intention: {
          title: "Intenção",
          description: "Defina intenções diárias claras que se alinham com seus valores e objetivos de longo prazo.",
        },
        reflection: {
          title: "Reflexão",
          description: "Termine cada dia com gratidão e insights que aceleram seu crescimento pessoal.",
        },
      },
    },
    appPreview: {
      ready: "Pronto para seu ritual?",
      dayStreak: "Sequência de dias",
      totalDays: "Total de dias",
      title: "Experimente o app",
      subtitle: "Design bonito e intuitivo que torna seu ritual diário um prazer. Acompanhe seu progresso, desbloqueie insights e observe sua evolução.",
      features: {
        guided: "Rituais guiados de manhã e noite",
        personalized: "Recomendações personalizadas",
        analytics: "Belas análises de progresso",
        offline: "Acesso offline",
      },
    },
    benefits: {
      title: "O poder dos rituais diários",
      subtitle: "Benefícios comprovados pela ciência que se acumulam com o tempo. Comece pequeno, seja constante e observe sua vida se transformar.",
      items: {
        clarity: {
          title: "Clareza mental",
          description: "Limpe sua mente e comece cada dia com foco e propósito.",
        },
        stress: {
          title: "Redução do estresse",
          description: "Construa resiliência e encontre calma no meio do caos da vida.",
        },
        sleep: {
          title: "Sono melhor",
          description: "Rituais noturnos que preparam sua mente e corpo para descansar.",
        },
        productivity: {
          title: "Maior produtividade",
          description: "Aproveite o máximo desempenho através de rotinas intencionais.",
        },
        relationships: {
          title: "Relacionamentos mais profundos",
          description: "Cultive presença e conexão em suas interações.",
        },
        growth: {
          title: "Crescimento pessoal",
          description: "Evolução contínua através da reflexão diária e aprendizado.",
        },
      },
    },
    unlock: {
      title: "Libere todo seu potencial",
      subtitle: "Recursos premium projetados para acelerar sua jornada de transformação.",
      premiumAccess: "Acesso Premium",
      premiumDesc: "Tudo o que você precisa para construir rituais duradouros",
      features: {
        unlimited: "Sessões guiadas ilimitadas",
        analytics: "Análises e insights avançados",
        personalized: "Planos de rituais personalizados",
        exclusive: "Biblioteca de conteúdo exclusivo",
        offline: "Acesso offline",
        support: "Suporte prioritário",
      },
    },
    testimonials: {
      title: "Amado por milhares",
      subtitle: "Junte-se a uma comunidade de pessoas que transformam suas vidas, um ritual de cada vez.",
      items: [
        {
          quote: "Evooluir mudou completamente minhas manhãs. Antes eu temia acordar, agora espero ansiosamente pelo meu ritual. Tornou-se a base do meu dia.",
          author: "Sarah M.",
          role: "Empreendedora",
        },
        {
          quote: "O recurso de reflexão noturna é incrível. Ganhei tanta clareza sobre o que importa na minha vida. Meus níveis de estresse diminuíram significativamente.",
          author: "Michael R.",
          role: "Engenheiro de software",
        },
        {
          quote: "Já tentei muitos apps de bem-estar, mas Evooluir é diferente. Não é apenas um app, é um companheiro na minha jornada de autodescoberta.",
          author: "Emma L.",
          role: "Professora",
        },
      ],
    },
    pricing: {
      title: "Preços simples e transparentes",
      subtitle: "Invista no seu bem-estar. Cancele quando quiser. Comece seu teste gratuito de 7 dias hoje.",
      monthly: "Mensal",
      yearly: "Anual",
      save: "Economize 33%",
      mostPopular: "Mais popular",
      perMonth: "/mês",
      billedAnnually: "Cobrado anualmente a $119.88",
      startRitual: "Comece seu ritual",
      trialText: "7 dias de teste grátis • Cancele quando quiser",
      guarantee: "Garantia de reembolso de 30 dias",
      guaranteeDesc: "Não satisfeito? Reembolsamos você, sem perguntas.",
      features: [
        "Sessões de rituais guiados ilimitadas",
        "Práticas guiadas de manhã e noite",
        "Recomendações de rituais personalizadas",
        "Análises de progresso avançadas",
        "Sugestões exclusivas de journaling",
        "Acesso offline ao conteúdo",
        "Suporte ao cliente prioritário",
        "Novo conteúdo adicionado semanalmente",
      ],
    },
    finalCta: {
      badge: "Sua transformação espera",
      title: "Pronto para",
      titleHighlight: "evoluir",
      subtitle: "Toda grande transformação começa com um único passo. Seu ritual espera. Comece seu teste gratuito de 7 dias e descubra o que é possível.",
      joinCount: "Junte-se a mais de 50.000 pessoas transformando suas vidas",
      featuredIn: "Destaque em",
    },
    footer: {
      tagline: "Transforme seus rituais diários",
      product: "Produto",
      features: "Recursos",
      pricing: "Preços",
      download: "Download",
      company: "Empresa",
      about: "Sobre",
      careers: "Carreiras",
      press: "Imprensa",
      support: "Suporte",
      helpCenter: "Central de ajuda",
      contact: "Contato",
      privacy: "Privacidade",
      terms: "Termos",
      copyright: "Todos os direitos reservados.",
      newsletter: {
        title: "Fique por dentro",
        subtitle: "Seja o primeiro a receber atualizações sobre nosso conteúdo mais recente, ofertas especiais e novos recursos.",
        placeholder: "Endereço de e-mail",
        button: "Inscrever-se",
        disclaimer: "Ao se inscrever, você concorda em receber e-mails de marketing. Você pode cancelar a inscrição a qualquer momento.",
      },
      getStarted: "Começar",
      tryFree: "Teste 7 dias grátis",
      ourPlans: "Nossos planos",
      sendGift: "Enviar presente",
      redeemCode: "Resgatar código",
      ourContent: "Nosso conteúdo",
      rituals: "Rituais diários",
      meditation: "Meditação",
      mindfulness: "Mindfulness",
      sleepContent: "Conteúdo de sono",
      articles: "Artigos",
      blog: "Blog",
      faq: "FAQ",
      accessibility: "Acessibilidade",
      myAccount: "Minha conta",
      login: "Entrar",
      getApp: "Baixe o app",
      consumerHealth: "Dados de saúde",
      privacyChoices: "Opções de privacidade",
    },
    signup: {
      title: "Cadastre-se",
      hasAccount: "Já tem uma conta?",
      logIn: "Entrar",
      getExperience: "Obtenha a experiência completa do Evooluir por",
      today: "hoje",
      perMonth: "por mês",
      billedAnnually: "cobrado anualmente em",
      afterTrial: "após seu teste de 7 dias",
      cancelAnytime: "Cancele a qualquer momento",
      benefit1: "Acesse toda nossa biblioteca de rituais, meditações e exercícios de mindfulness",
      benefit2: "Receba conteúdo de bem-estar personalizado diariamente",
      benefit3: "Conteúdo inspirador diário para começar bem o dia",
      name: "Nome Completo",
      emailAddress: "Endereço de e-mail",
      password: "Senha",
      characters: "caracteres",
      newsletterQuestion: "Quer ficar por dentro das novidades?",
      newsletterSubtext: "Junte-se à nossa lista de e-mails e seja o primeiro a saber de ofertas especiais.",
      yes: "Sim",
      no: "Não",
      areYouSure: "Tem certeza?",
      missOut: "Você vai perder descontos exclusivos e as últimas notícias do Evooluir.",
      agreeToTerms: "Eu concordo com os",
      termsLink: "Termos e Condições",
      andAcknowledge: "do Evooluir e reconheço a",
      privacyLink: "Política de Privacidade",
      createAccount: "Criar conta",
      processing: "Processando...",
    },
    checkout: {
      signUpTitle: "Crie sua conta",
      signUpSubtitle: "Cadastre-se para começar sua jornada de bem-estar e desbloquear sua assinatura.",
      emailLabel: "E-mail",
      emailPlaceholder: "Digite seu e-mail",
      passwordLabel: "Senha",
      passwordPlaceholder: "Crie uma senha",
      signUpButton: "Cadastrar",
      orContinueWith: "Ou continuar com",
      continueSignUp: "Continuar para cadastro",
      continueEmail: "Continuar com e-mail",
      continueGoogle: "Continuar com Google",
      continueApple: "Continuar com Apple",
      thenSelectPlan: "Depois selecione seu plano",
      afterSignupText: "Após o cadastro, você selecionará seu plano e completará o pagamento para começar sua jornada de bem-estar.",
      termsText: "Ao se cadastrar, você concorda com nossos Termos de Serviço e Política de Privacidade.",
      planTitle: "Seu plano selecionado",
      planMonthly: "Plano mensal",
      planYearly: "Plano anual",
      perMonth: "/mês",
      billedAnnually: "Cobrado anualmente",
      startTrial: "Iniciar teste de 7 dias",
      processing: "Processando...",
      included: "O que está incluído:",
      features: [
        "Sessões guiadas ilimitadas",
        "Rituais de manhã e noite",
        "Recomendações personalizadas",
        "Análises de progresso",
      ],
    },
    success: {
      welcomeBadge: "Bem-vindo ao Evooluir",
      title: "Seu ritual começa agora",
      subtitle: "Obrigado por se juntar à nossa comunidade de indivíduos conscientes. Sua jornada de transformação começa hoje.",
      premiumTitle: "Evooluir Premium",
      subscriptionActive: "Sua assinatura está ativa",
      features: [
        "Acesso ilimitado a todas as sessões de rituais",
        "Meditações premium guiadas por áudio",
        "Análises de progresso avançadas",
        "Conteúdo exclusivo atualizado semanalmente",
      ],
      downloadTitle: "Baixe o app",
      downloadSubtitle: "Continue seu ritual no celular",
      downloadOn: "Baixar na",
      appStore: "App Store",
      getItOn: "Disponível no",
      googlePlay: "Google Play",
      goToDashboard: "Ir para o painel",
    },
    notFound: {
      title: "Página não encontrada",
      subtitle: "A página que você procura não existe ou foi movida.",
      backHome: "Voltar ao início",
    },
    cancel: {
      title: "Pagamento cancelado",
      subtitle: "Não se preocupe, nenhuma cobrança foi feita. Escolha um plano quando quiser.",
      backToPlans: "Voltar aos planos",
    },
    dashboard: {
      welcome: "Bem-vindo de volta",
      subtitle: "Continue sua jornada de transformação",
      subscription: "Assinatura",
      status: "Status",
      active: "Ativo",
      inactive: "Inativo",
      plan: "Plano",
      monthly: "Mensal",
      yearly: "Anual",
      nextBilling: "Próxima cobrança",
      manage: "Gerenciar",
      noSubscription: "Você ainda não tem uma assinatura ativa.",
      quickStats: "Estatísticas rápidas",
      currentStreak: "Sequência atual",
      days: "dias",
      totalRituals: "Total de rituais",
      rituals: "rituais",
      memberSince: "Membro desde",
      getApp: "Obtenha o app",
      getAppDesc: "Continue sua jornada ritual no celular",
      account: "Conta",
      settings: "Configurações",
      billingHistory: "Histórico de cobranças",
      logout: "Sair",
    },
  },
};

export type Translations = typeof translations.en;
